import { Modal } from "@circle-react-uikit/ModalV2";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { CreateSpaceV4 } from "@circle-react/components/Spaces/CreateSpaceV4";
import { NewV2 } from "@circle-react/components/Spaces/NewV2";
import { FORM_ID } from "@circle-react/components/Spaces/NewV2/NewForm";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import type { SpaceType } from "@circle-react/types/Space";
import { useState } from "react";

import { t } from "@/i18n-js/instance";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";
import { isCommunityAdmin } from "@/react/helpers/communityMemberHelpers";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

import type { SpacePrefillData } from "../../SettingsApp/Spaces/helpers/spacePrefillHelpers";
import { SpaceTypePickerModalComponent } from "../SpaceTypePickerModal";

interface AddSpaceModalComponentProps {
  postType?: SpaceType;
  prefillData?: SpacePrefillData;
  spaceGroupId?: number;
}

const AddSpaceModalComponent = ({
  spaceGroupId,
  prefillData,
  postType,
}: AddSpaceModalComponentProps) => {
  const drawer = useModal();
  const [isLoading, setIsLoading] = useState(false);
  return (
    <Modal
      isOpen={drawer.visible}
      onClose={drawer.hide}
      contentPortalId="create-space-content-portal"
      title={t("create_space")}
    >
      <Modal.Overlay />
      <Modal.Content size="md">
        <Modal.Header className="!px-6 md:!px-8">
          <Modal.Title size="sm">{t("create_space")}</Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton onClick={drawer.hide} />
          </Modal.HeaderActions>
        </Modal.Header>
        <Modal.Body className="!px-6 !py-1 md:!px-8">
          <NewV2
            onClose={drawer.hide}
            setExternalLoadingState={setIsLoading}
            currentSpaceGroupId={spaceGroupId}
            post_type={postType}
            prefillData={prefillData}
            renderHeader={() => <>{}</>}
            renderFooter={() => <>{}</>}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={isLoading} type="submit" form={FORM_ID} full large>
            {t(isLoading ? "creating" : "create_space")}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

const AddSpaceModal = ModalManager.create(
  ({ spaceGroupId, postType, prefillData }: AddSpaceModalComponentProps) => {
    const drawer = useModal();
    const { currentCommunityMember } = usePunditUserContext();
    const isV4ModalsEnabled = useIsCircleV4FeatureEnabled(
      "circle_v4_modals_enabled",
    );

    if (isV4ModalsEnabled && isCommunityAdmin(currentCommunityMember)) {
      return (
        <CreateSpaceV4
          open={drawer.visible}
          onOpenChange={open => {
            if (!open) void drawer.hide();
          }}
          currentSpaceGroupId={spaceGroupId}
          initialPostType={postType}
          prefillData={prefillData}
        />
      );
    }

    if (isCommunityAdmin(currentCommunityMember)) {
      return (
        <SpaceTypePickerModalComponent
          spaceGroupId={spaceGroupId}
          selectedPostType={postType}
          prefillData={prefillData}
        />
      );
    }
    return (
      <AddSpaceModalComponent
        spaceGroupId={spaceGroupId}
        postType={postType}
        prefillData={prefillData}
      />
    );
  },
);

export const useAddSpaceModal = () => useModal(AddSpaceModal);
